import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';

function formatNum(n) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString();
}

/** Format seconds as whole minutes, e.g. 90 -> "2 min", 45 -> "1 min", 0 -> "0 min" */
function formatDurationMinutes(seconds) {
  if (seconds == null || seconds === '') return '—';
  const sec = Number(seconds);
  if (Number.isNaN(sec)) return '—';
  const mins = Math.round(sec / 60);
  return mins === 1 ? '1 min' : `${mins} min`;
}

function DetailTable({ rows }) {
  return (
    <table className="min-w-full">
      <tbody className="divide-y divide-gray-100">
        {rows.map(([label, value], i) => (
          <tr key={i}>
            <td className="py-2 pr-4 text-sm text-gray-600 align-top w-1/2">{label}</td>
            <td className="py-2 text-sm font-medium text-gray-900 align-top">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DetailCard({ title, description, children, className = '' }) {
  return (
    <div className={`rounded-lg border border-gray-200 bg-gray-50/50 p-4 ${className}`}>
      <h4 className="text-sm font-semibold text-gray-800 mb-1">{title}</h4>
      {description && <p className="text-xs text-gray-500 mb-3">{description}</p>}
      {children}
    </div>
  );
}

export default function PipelineRuns() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRunId, setExpandedRunId] = useState(null);
  const limit = 20;

  const loadRuns = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authService.getPipelineRuns(limit);
      setRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'We could not load the list of pipeline runs. Please try again.');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const statusLabel = (status) => {
    if (status === 'success') return 'Completed successfully';
    if (status === 'failed') return 'Failed';
    if (status === 'running') return 'In progress';
    return status || '—';
  };

  const statusBadge = (status) => {
    const styles = {
      running: 'bg-sky-50 text-sky-700 border border-sky-200',
      success: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      failed: 'bg-rose-50 text-rose-700 border border-rose-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
        {statusLabel(status)}
      </span>
    );
  };

  const displayError = (run) => {
    const msg = run.error_explanation || run.error_message;
    if (!msg) return '—';
    return msg;
  };

  const hasStep1Data = (run) => {
    const s1 = run.step1;
    if (!s1) return false;
    return [s1.candidates_upserted, s1.companies_upserted, s1.activities_upserted, s1.jobs_upserted, s1.placements_upserted].some(
      (v) => v != null && v !== ''
    );
  };

  const hasStep2Data = (run) => {
    const s2 = run.step2;
    if (!s2) return false;
    return [s2.pairs_computed, s2.rows_upserted, s2.duration_seconds].some((v) => v != null && v !== '');
  };

  const hasStep3Data = (run) => {
    const s3 = run.step3;
    if (!s3) return false;
    return [s3.rows_upserted, s3.persons_upserted, s3.companies_upserted, s3.duration_seconds].some((v) => v != null && v !== '');
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Pipeline runs</h2>
            <p className="mt-1 text-sm text-gray-500 max-w-2xl">
              Each run has three steps: data load, recruiter fit matching, and scoring. Expand a run to see full details.
            </p>
          </div>
          <button
            onClick={loadRuns}
            disabled={loading}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#00C6AE] hover:bg-[#00b39b] rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Loading…
              </>
            ) : (
              'Refresh'
            )}
          </button>
        </div>
      </div>

      <div className="px-6 py-4">
        {error && (
          <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 text-sm" role="alert">
            {error}
          </div>
        )}

        {loading && runs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <span className="inline-block w-10 h-10 border-2 border-[#00C6AE] border-t-transparent rounded-full animate-spin mb-3" />
            <span className="text-sm">Loading pipeline runs…</span>
          </div>
        ) : runs.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-gray-500">No pipeline runs have been recorded yet.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Started</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Completed</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Step 1</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Step 2</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Step 3</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[140px]">Error</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {runs.map((run) => (
                    <React.Fragment key={run.run_id}>
                      <tr className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatDate(run.started_at)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(run.completed_at)}</td>
                        <td className="px-4 py-3">{statusBadge(run.status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {hasStep1Data(run) ? (
                            <span className="text-gray-600">
                              {formatNum(run.step1.candidates_upserted)} contacts, {formatNum(run.step1.companies_upserted)} companies
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {hasStep2Data(run) ? (
                            <span className="text-gray-600">
                              {formatNum(run.step2.rows_upserted)} matches · {formatDurationMinutes(run.step2.duration_seconds)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {hasStep3Data(run) ? (
                            <span className="text-gray-600">
                              {formatNum(run.step3.persons_upserted ?? run.step3.rows_upserted)} scored · {formatDurationMinutes(run.step3.duration_seconds)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm max-w-[200px]">
                          {run.status === 'failed' ? (
                            <span className="text-rose-600 line-clamp-2" title={displayError(run)}>{displayError(run)}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setExpandedRunId(expandedRunId === run.run_id ? null : run.run_id)}
                            className="text-sm font-medium text-[#00C6AE] hover:text-[#00b39b] focus:outline-none focus:ring-2 focus:ring-[#00C6AE]/30 rounded px-2 py-1"
                            aria-expanded={expandedRunId === run.run_id}
                          >
                            {expandedRunId === run.run_id ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {expandedRunId === run.run_id && (
                        <tr key={`${run.run_id}-details`}>
                          <td colSpan={8} className="p-0 bg-gray-50/80">
                            <div className="p-6 border-t border-gray-200">
                              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
                                  <h3 className="text-base font-semibold text-gray-900">Run details</h3>
                                  <p className="text-sm text-gray-500 mt-0.5">
                                    Started {formatDate(run.started_at)}
                                    {run.completed_at && ` · Completed ${formatDate(run.completed_at)}`}
                                  </p>
                                </div>
                                <div className="p-5">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <DetailCard
                                      title="Step 1: Data loaded"
                                      description="Records added or updated from your source."
                                    >
                                      <DetailTable
                                        rows={[
                                          ['Contacts', formatNum(run.step1?.candidates_upserted)],
                                          ['Companies', formatNum(run.step1?.companies_upserted)],
                                          ['Activities', formatNum(run.step1?.activities_upserted)],
                                          ['Jobs', formatNum(run.step1?.jobs_upserted)],
                                          ['Placements', formatNum(run.step1?.placements_upserted)]
                                        ]}
                                      />
                                    </DetailCard>
                                    <DetailCard
                                      title="Step 2: Recruiter fit"
                                      description="Matching candidates to recruiters and managers by focus area."
                                    >
                                      <DetailTable
                                        rows={[
                                          ['Recruiters/managers with updated focus', formatNum(run.step2?.recruiter_delta_count)],
                                          ['Candidates included in matching', formatNum(run.step2?.candidate_delta_count)],
                                          ['Total match pairs computed', formatNum(run.step2?.pairs_computed)],
                                          ['Matches saved', formatNum(run.step2?.rows_upserted)],
                                          ['Time', formatDurationMinutes(run.step2?.duration_seconds)]
                                        ]}
                                      />
                                    </DetailCard>
                                    <DetailCard
                                      title="Match confidence (Step 2)"
                                      description="Semantic matches by confidence level."
                                    >
                                      <DetailTable
                                        rows={[
                                          ['High confidence', formatNum(run.step2?.matches_high)],
                                          ['Medium confidence', formatNum(run.step2?.matches_medium)],
                                          ['Low confidence', formatNum(run.step2?.matches_low)]
                                        ]}
                                      />
                                    </DetailCard>
                                    <DetailCard
                                      title="Step 3: Scoring"
                                      description="Candidates and companies scored for the candidate graph."
                                    >
                                      <DetailTable
                                        rows={[
                                          ['Candidates scored and presented', formatNum(run.step3?.persons_upserted ?? run.step3?.rows_upserted)],
                                          ['Companies scored', formatNum(run.step3?.companies_upserted)],
                                          ['Total records updated', formatNum(run.step3?.rows_upserted)],
                                          ['Time', formatDurationMinutes(run.step3?.duration_seconds)]
                                        ]}
                                      />
                                    </DetailCard>
                                  </div>
                                  {run.status === 'failed' && (run.error_explanation || run.error_message) && (
                                    <div className="mt-5 p-4 rounded-lg bg-rose-50 border border-rose-200" role="alert">
                                      <h4 className="text-sm font-semibold text-rose-800 mb-1">What went wrong</h4>
                                      <p className="text-sm text-rose-700">{displayError(run)}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
